import React, { useState, useEffect, useCallback } from 'react';
import { getGateways, createRebillJob, getRebillJobs, startRebillJob, stopRebillJob, resumeRebillJob, getAvailableClients } from '../api';
import JobDetailsModal from './JobDetailsModal';

const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function RebillTab({ socket }) {
  const [gateways, setGateways] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Job creation form
  const [selectedGateways, setSelectedGateways] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [durationDays, setDurationDays] = useState(1);
  const [pricepoints, setPricepoints] = useState([{ amount: '29.99', percentage: '100' }]);
  const [jobName, setJobName] = useState('');
  const [creating, setCreating] = useState(false);
  const [cycleFilter, setCycleFilter] = useState('');
  const [cycleCounts, setCycleCounts] = useState([]);
  const [detailsJobId, setDetailsJobId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [gRes, jRes] = await Promise.all([getGateways(), getRebillJobs()]);
      if (gRes.success) setGateways(gRes.data);
      if (jRes.success) setJobs(jRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 10000); return () => clearInterval(i); }, [fetchData]);

  // Fetch available clients count when date filter or cycle changes
  useEffect(() => {
    if (dateFrom && dateTo) {
      const params = `date_from=${dateFrom}&date_to=${dateTo}${cycleFilter !== '' ? `&cycle=${cycleFilter}` : ''}`;
      getAvailableClients(params)
        .then(r => {
          if (r.success) {
            setAvailableCount(r.data.total || 0);
            setCycleCounts(r.data.cycle_counts || []);
          }
        })
        .catch(() => {});
    }
  }, [dateFrom, dateTo, cycleFilter]);

  useEffect(() => {
    if (!socket) return;
    const r = () => fetchData();
    socket.on('job_completed', r); socket.on('job_declined', r);
    return () => { socket.off('job_completed', r); socket.off('job_declined', r); };
  }, [socket, fetchData]);

  const addPricepoint = () => setPricepoints([...pricepoints, { amount: '', percentage: '' }]);
  const removePricepoint = (i) => setPricepoints(pricepoints.filter((_, idx) => idx !== i));
  const updatePricepoint = (i, field, val) => {
    const updated = [...pricepoints];
    updated[i][field] = val;
    setPricepoints(updated);
  };

  const totalPercentage = pricepoints.reduce((s, p) => s + (parseFloat(p.percentage) || 0), 0);

  const handleCreateJob = async () => {
    if (totalPercentage !== 100) { alert('Pricepoint percentages must total 100%'); return; }
    if (!dateFrom || !dateTo) { alert('Select date range'); return; }
    if (selectedGateways.length === 0) { alert('Select at least one gateway'); return; }

    setCreating(true);
    try {
      await createRebillJob({
        name: jobName || `Job ${new Date().toLocaleDateString()}`,
        gateway_ids: selectedGateways,
        pricepoints: pricepoints.map(p => ({ amount: parseFloat(p.amount), percentage: parseFloat(p.percentage) })),
        duration_days: durationDays,
        date_from: dateFrom,
        date_to: dateTo,
        cycle: cycleFilter,
      });
      fetchData();
      setJobName(''); setSelectedGateways([]); setDateFrom(''); setDateTo('');
      setPricepoints([{ amount: '29.99', percentage: '100' }]);
    } catch (e) { alert(e.message); }
    finally { setCreating(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Rebill — Manual Job Creator</h2>

      {/* Job Creation Form */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
        <h3 className="text-lg font-semibold text-white">Create New Rebill Job</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Job Name</label>
            <input type="text" value={jobName} onChange={e => setJobName(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none" placeholder="My Rebill Job" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Date From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Date To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Duration (days)</label>
            <input type="number" min="1" max="30" value={durationDays} onChange={e => setDurationDays(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none" />
          </div>
        </div>

        {/* Cycle Selection */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Client Cycle</label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: '', label: 'All Pending', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
              { value: '0', label: 'Never Billed', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
              { value: '1', label: 'Cycle 1', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
              { value: '2', label: 'Cycle 2', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
              { value: '3', label: 'Cycle 3', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
              { value: '4', label: 'Cycle 4', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
              { value: 'no_gateway', label: 'No Gateway', color: 'bg-slate-700/50 border-slate-600 text-slate-400' },
            ].map(opt => {
              const count = opt.value === '' ? null : cycleCounts.find(c => String(c.rebill_cycle) === opt.value)?.count || 0;
              const isActive = cycleFilter === opt.value;
              return (
                <button key={opt.value} onClick={() => setCycleFilter(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${isActive ? 'bg-blue-600/20 border-blue-500 text-blue-400' : opt.color}`}>
                  {opt.label} {count !== null && <span className="ml-1 text-[10px] opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {dateFrom && dateTo && (
          <p className="text-sm text-blue-400">📊 {availableCount} clients available {cycleFilter !== '' ? `(${cycleFilter === '0' ? 'Never Billed' : `Cycle ${cycleFilter}`})` : '(all pending)'}</p>
        )}

        {/* Pricepoints */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-slate-400">Pricepoints (must total 100%)</label>
            <span className={`text-xs font-bold ${totalPercentage === 100 ? 'text-green-400' : 'text-red-400'}`}>
              Total: {totalPercentage}%
            </span>
          </div>
          {pricepoints.map((pp, i) => (
            <div key={i} className="flex items-center space-x-2 mb-2">
              <input type="number" step="0.01" placeholder="Amount ($)" value={pp.amount}
                onChange={e => updatePricepoint(i, 'amount', e.target.value)}
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none" />
              <input type="number" min="0" max="100" placeholder="%" value={pp.percentage}
                onChange={e => updatePricepoint(i, 'percentage', e.target.value)}
                className="w-20 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none" />
              <span className="text-xs text-slate-500">%</span>
              {pricepoints.length > 1 && (
                <button onClick={() => removePricepoint(i)} className="text-red-400 hover:text-red-300 text-sm">✕</button>
              )}
            </div>
          ))}
          <button onClick={addPricepoint} className="text-xs text-blue-400 hover:text-blue-300">+ Add Pricepoint</button>
        </div>

        {/* Gateway Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-slate-400">Select Gateways</label>
            <div className="flex space-x-2">
              <button onClick={() => setSelectedGateways(gateways.filter(g => g.is_active).map(g => g.id))} className="px-2 py-1 bg-blue-600 text-white rounded text-[10px]">Select All</button>
              <button onClick={() => setSelectedGateways([])} className="px-2 py-1 bg-slate-600 text-white rounded text-[10px]">Unselect All</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {gateways.filter(g => g.is_active).map(gw => (
              <button key={gw.id}
                onClick={() => setSelectedGateways(p => p.includes(gw.id) ? p.filter(x => x !== gw.id) : [...p, gw.id])}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  selectedGateways.includes(gw.id) ? 'bg-blue-600/20 border-blue-500 text-blue-400' : 'bg-slate-700/50 border-slate-600 text-slate-400'
                }`}>{gw.processor} - {gw.bank} - {gw.dba}{gw.company ? ` - ${gw.company}` : ''}</button>
            ))}
          </div>
        </div>

        <button onClick={handleCreateJob} disabled={creating || totalPercentage !== 100}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-bold">
          {creating ? 'Creating...' : '🚀 Create & Schedule Job'}
        </button>
      </div>

      {/* Active/History Jobs */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Rebill Jobs</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Progress</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Success</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Declined</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Revenue</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Approval</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {jobs.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No jobs created yet. Create one above.</td></tr>
              ) : jobs.map(j => (
                <tr key={j.id} className="hover:bg-slate-700/30">
                  <td className="px-4 py-2.5 text-white text-xs">{j.name || `Job #${j.id}`}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      j.status === 'running' ? 'bg-blue-500/10 text-blue-400' :
                      j.status === 'completed' ? 'bg-green-500/10 text-green-400' :
                      j.status === 'cancelled' ? 'bg-red-500/10 text-red-400' :
                      'bg-yellow-500/10 text-yellow-400'
                    }`}>{j.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-center text-slate-300 text-xs">{j.processed}/{j.total_clients}</td>
                  <td className="px-4 py-2.5 text-center text-green-400 text-xs">{j.success_count}</td>
                  <td className="px-4 py-2.5 text-center text-red-400 text-xs">{j.declined_count}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-400 font-bold text-xs">{fmt(j.total_revenue)}</td>
                  <td className="px-4 py-2.5 text-center">
                    {(j.success_count + j.declined_count) > 0 ? (
                      <span className={`text-xs font-bold ${
                        ((j.success_count / (j.success_count + j.declined_count)) * 100) >= 50 ? 'text-green-400' :
                        ((j.success_count / (j.success_count + j.declined_count)) * 100) >= 30 ? 'text-yellow-400' : 'text-red-400'
                      }`}>{((j.success_count / (j.success_count + j.declined_count)) * 100).toFixed(1)}%</span>
                    ) : <span className="text-slate-500 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center space-x-1">
                    {j.status === 'pending' && (
                      <button onClick={() => startRebillJob(j.id).then(fetchData)}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs">Start</button>
                    )}
                    {j.status === 'running' && (
                      <button onClick={() => stopRebillJob(j.id).then(fetchData)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs">Stop</button>
                    )}
                    {j.status === 'paused' && (
                      <button onClick={() => resumeRebillJob(j.id).then(fetchData)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs">Resume</button>
                    )}
                    <button onClick={() => setDetailsJobId(j.id)}
                      className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs">Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Details Modal */}
      {detailsJobId && (
        <JobDetailsModal jobId={detailsJobId} onClose={() => setDetailsJobId(null)} />
      )}
    </div>
  );
}

export default RebillTab;
